// utils/speechRecognition.js

/**
 * Kontrollera om Web Speech API stöds
 */
export const isSpeechRecognitionSupported = () => {
  return !!(
    window.SpeechRecognition ||
    window.webkitSpeechRecognition ||
    window.mozSpeechRecognition ||
    window.msSpeechRecognition
  );
};

/**
 * Hämta SpeechRecognition-konstruktorn
 */
export const getSpeechRecognition = () => {
  return (
    window.SpeechRecognition ||
    window.webkitSpeechRecognition ||
    window.mozSpeechRecognition ||
    window.msSpeechRecognition
  );
};

/**
 * Kontrollera browser-stöd och returnera detaljerad info
 */
export const getBrowserSpeechSupport = () => {
  const isSupported = isSpeechRecognitionSupported();
  const userAgent = navigator.userAgent.toLowerCase();

  // Identifiera webbläsare
  const isChrome = userAgent.includes('chrome') && !userAgent.includes('edg');
  const isEdge = userAgent.includes('edg');
  const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');
  const isFirefox = userAgent.includes('firefox');

  let browser = 'unknown';
  let supportLevel = 'none';
  let message = '';

  if (isChrome) {
    browser = 'chrome';
    supportLevel = isSupported ? 'full' : 'none';
    message = isSupported
      ? 'Chrome har fullt stöd för röstinmatning.'
      : 'Röstinmatning kräver HTTPS i produktion.';
  } else if (isEdge) {
    browser = 'edge';
    supportLevel = isSupported ? 'full' : 'none';
    message = isSupported
      ? 'Edge har fullt stöd för röstinmatning.'
      : 'Röstinmatning kräver HTTPS i produktion.';
  } else if (isSafari) {
    browser = 'safari';
    supportLevel = isSupported ? 'partial' : 'none';
    message = isSupported
      ? 'Safari har partiellt stöd. Röstinmatning kan vara opålitlig.'
      : 'Safari stödjer inte röstinmatning på denna enhet.';
  } else if (isFirefox) {
    browser = 'firefox';
    supportLevel = 'none';
    message = 'Firefox stödjer inte Web Speech API. Använd textinmatning istället.';
  }

  return {
    isSupported,
    browser,
    supportLevel,
    message
  };
};

/**
 * Kontrollera om mikrofon-tillstånd finns
 */
export const checkMicrophonePermission = async () => {
  try {
    const permission = await navigator.permissions.query({ name: 'microphone' });
    return permission.state; // 'granted', 'denied', eller 'prompt'
  } catch (err) {
    // permissions.query stöds inte i alla browsers
    return 'unknown';
  }
};

/**
 * Begär mikrofonåtkomst
 */
export const requestMicrophoneAccess = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Stoppa streamen direkt - vi behövde bara tillståndet
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch (err) {
    console.error('Mikrofonåtkomst nekad:', err);
    return false;
  }
};

/**
 * Kontrollera om enheten är en mobil/tablet
 * Används för att avgöra om continuous mode ska vara på eller av
 */
export const isMobileDevice = () => {
  // Kolla user agent för mobila enheter
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = [
    'android',
    'webos',
    'iphone',
    'ipad',
    'ipod',
    'blackberry',
    'windows phone',
    'opera mini',
    'mobile'
  ];

  const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));

  // Kolla även skärmbredd som backup (tablets kan ha bred skärm men ändå vara mobil)
  const isSmallScreen = window.innerWidth <= 768;

  // Kolla touch capability
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Anses som mobil om user agent säger det, ELLER om det är liten skärm med touch
  return isMobileUA || (isSmallScreen && hasTouch);
};
